import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Auto-attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('staff_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ==================== TYPES ====================
interface TopClient { user_id: number; name: string; email: string; total_spent: number; total_orders: number; last_visit: string | null; loyalty_points: number; }
interface ProductPerf { product_id: number; product_name: string; category: string; total_sold: number; total_revenue: number; }
interface ClientSegment { segment: string; count: number; avg_spent: number; description: string; }
interface SocialStats { total_attempts: number; total_interactions: number; total_matches: number; active_social_users: number; }
interface ClientDetail { id: number; email: string; first_name: string; last_name: string; phone: string; photo_url: string; allergies: string; interests: string; total_spent: number; total_orders: number; loyalty_points: number; last_visit: string | null; }

// ==================== MAIN APP ====================
function App() {
  const [token, setToken] = useState(localStorage.getItem('staff_token') || '');
  const [userRole, setUserRole] = useState(localStorage.getItem('staff_role') || '');
  const [userName, setUserName] = useState(localStorage.getItem('staff_name') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [page, setPage] = useState<'dashboard' | 'clients' | 'products' | 'social' | 'notifications' | 'config' | 'gestion-produits'>('dashboard');

  // Dashboard data
  const [topClients, setTopClients] = useState<TopClient[]>([]);
  const [productPerfs, setProductPerfs] = useState<ProductPerf[]>([]);
  const [segments, setSegments] = useState<ClientSegment[]>([]);
  const [socialStats, setSocialStats] = useState<SocialStats | null>(null);
  const [revenueToday, setRevenueToday] = useState(0);
  const [checkInsToday, setCheckInsToday] = useState(0);
  const [reservationsToday, setReservationsToday] = useState(0);
  const [loading, setLoading] = useState(false);

  // Clients
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClientDetail | null>(null);
  const [clientConsumption, setClientConsumption] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Configuration (admin only)
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPassword, setNewClientPassword] = useState('');
  const [newClientFirstName, setNewClientFirstName] = useState('');
  const [newClientLastName, setNewClientLastName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductCategory, setNewProductCategory] = useState('wine');
  const [newProductPrice, setNewProductPrice] = useState('');
  const [newProductDescription, setNewProductDescription] = useState('');
  const [newProductSubCategory, setNewProductSubCategory] = useState('');
  const [newProductAvailable, setNewProductAvailable] = useState(true);
  const [newProductFeatured, setNewProductFeatured] = useState(false);
  const [newProductCostPrice, setNewProductCostPrice] = useState('');
  const [newProductCateringCategory, setNewProductCateringCategory] = useState('');
  const [newProductSortOrder, setNewProductSortOrder] = useState('0');
  const [csvImportResult, setCsvImportResult] = useState<string>('');
  const [csvImportFile, setCsvImportFile] = useState<File | null>(null);
  const [quickCsvFile, setQuickCsvFile] = useState<File | null>(null);
  const [quickCsvResult, setQuickCsvResult] = useState<string>('');

  // Gestion Produits (admin only)
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState<string>('all');
  const [editProductId, setEditProductId] = useState<number | null>(null);
  const [editProductData, setEditProductData] = useState<any>({});

  const isLoggedIn = !!token && (userRole === 'staff' || userRole === 'admin');

  const login = async () => {
    setLoginError('');
    try {
      const res = await api.post('/auth/login', { email, password });
      const userData = res.data.user;
      const role = userData.role;
      const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();

      // Refuser l'acces aux clients
      if (role !== 'staff' && role !== 'admin') {
        setLoginError('Acces refuse : ce dashboard est reserve au personnel.');
        return;
      }

      setToken(res.data.access_token);
      setUserRole(role);
      setUserName(fullName);
      localStorage.setItem('staff_token', res.data.access_token);
      localStorage.setItem('staff_role', role);
      localStorage.setItem('staff_name', fullName);
    } catch (e: any) {
      setLoginError('Identifiants invalides');
    }
  };

  const logout = () => {
    setToken('');
    setUserRole('');
    setUserName('');
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_role');
    localStorage.removeItem('staff_name');
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/staff/dashboard');
      const d = res.data;
      setTopClients(d.top_clients);
      setProductPerfs(d.product_performances);
      setSegments(d.client_segments);
      setSocialStats(d.social_stats);
      setRevenueToday(d.total_revenue_today);
      setCheckInsToday(d.total_check_ins_today);
      setReservationsToday(d.total_reservations_today);
    } catch (e) {} finally { setLoading(false); }
  };

  const loadClients = async (search?: string) => {
    try {
      const res = await api.get('/staff/clients', { params: { search } });
      setClients(res.data);
    } catch (e) {}
  };

  const loadClientDetail = async (userId: number) => {
    try {
      const [detailRes, consumptionRes] = await Promise.all([
        api.get(`/staff/clients/${userId}`),
        api.get(`/staff/clients/${userId}/consumption`),
      ]);
      setSelectedClient(detailRes.data);
      setClientConsumption(consumptionRes.data);
    } catch (e) {}
  };

  const sendBirthdayNotifs = async () => {
    try {
      const res = await api.post('/staff/send-birthday-notifications');
      alert(res.data.message);
    } catch (e: any) { alert('Erreur: ' + e.response?.data?.detail); }
  };

  const sendPromo = async () => {
    const title = prompt('Titre de la promotion');
    const message = prompt('Message');
    const segment = prompt('Segment (all, vip)', 'all');
    if (!title || !message) return;
    try {
      const res = await api.post('/staff/send-promotion', null, { params: { title, message, segment } });
      alert(res.data.message);
    } catch (e: any) { alert('Erreur: ' + e.response?.data?.detail); }
  };

  useEffect(() => {
    if (isLoggedIn) loadDashboard();
  }, [isLoggedIn]);

  const loadAllProducts = async () => {
    try {
      const res = await api.get('/products/', { params: { available_only: false } });
      setAllProducts(res.data);
    } catch (e) {}
  };

  const handleUpdateProduct = async (id: number) => {
    try {
      await api.put(`/products/${id}`, editProductData);
      setEditProductId(null);
      setEditProductData({});
      loadAllProducts();
      alert('Produit mis a jour');
    } catch (e: any) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteProduct = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Desactiver ce produit ?')) return;
    try {
      await api.delete(`/products/${id}`);
      loadAllProducts();
      alert('Produit desactive');
    } catch (e: any) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if ((page === 'clients' || page === 'config') && isLoggedIn) loadClients();
    if (page === 'gestion-produits' && isLoggedIn) loadAllProducts();
  }, [page]);

  // ===== Configuration helpers (admin) =====
  const handleCreateClient = async () => {
    try {
      await api.post('/staff/admin/clients', {
        email: newClientEmail,
        password: newClientPassword,
        first_name: newClientFirstName,
        last_name: newClientLastName,
        phone: newClientPhone,
      });
      setShowAddClient(false);
      setNewClientEmail(''); setNewClientPassword(''); setNewClientFirstName(''); setNewClientLastName(''); setNewClientPhone('');
      loadClients();
      alert('Client cree avec succes');
    } catch (e: any) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDisableClient = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Desactiver ce client ?')) return;
    try {
      await api.delete(`/staff/admin/clients/${id}`);
      loadClients();
      alert('Client desactive');
    } catch (e: any) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleExportClients = async () => {
    try {
      const res = await api.get('/staff/admin/export/clients', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert('Erreur export: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleCreateProduct = async () => {
    try {
      await api.post('/products/', {
        name: newProductName,
        category: newProductCategory,
        price: parseFloat(newProductPrice),
        description: newProductDescription,
        sub_category: newProductSubCategory,
        is_available: newProductAvailable,
        is_featured: newProductFeatured,
      });
      setShowAddProduct(false);
      setNewProductName(''); setNewProductCategory('wine'); setNewProductPrice(''); setNewProductDescription(''); setNewProductSubCategory('');
      setNewProductAvailable(true); setNewProductFeatured(false);
      loadAllProducts();
      alert('Produit cree avec succes');
    } catch (e: any) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleImportCsv = async (file: File | null, setResult: (msg: string) => void) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/staff/admin/products/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data;
      let msg = `${data.imported_count || 0} produits importes`;
      if (data.errors && data.errors.length > 0) {
        msg += ` | Erreurs: ${data.errors.join(', ')}`;
      }
      setResult(msg);
    } catch (e: any) {
      setResult('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ==================== LOGIN ====================
  if (!isLoggedIn) {
    return (
      <div style={styles.page}>
        <div style={styles.loginCard}>
          <div style={{ width: 120, height: 120, borderRadius: '50%', overflow: 'hidden', margin: '8px auto 16px', border: '3px solid #C9A96E', boxShadow: '0 0 20px rgba(201,169,110,0.5), 0 0 50px rgba(201,169,110,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/buddha_v3_login_glow_256.png" alt="Le Siam" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <h1 style={styles.logo}>Le Siam</h1>
          <p style={styles.logoSub}>Dashboard Staff</p>
          <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
          <input style={styles.input} type="password" placeholder="Mot de passe" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} />
          <button style={styles.loginBtn} onClick={login}>Connexion Staff</button>
          {loginError && <p style={styles.loginError}>{loginError}</p>}
        </div>
      </div>
    );
  }

  // ==================== NAVIGATION ====================
  const allNavItems = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'clients', label: 'Clients', icon: '👥' },
    { key: 'gestion-produits', label: 'Gestion Produits', icon: '📦' },
    { key: 'social', label: 'Social', icon: '💬' },
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'config', label: 'Configuration', icon: '⚙️' },
  ] as const;

  const navItems = allNavItems.filter(item => {
    if (item.key === 'config' && userRole !== 'admin') return false;
    if (item.key === 'gestion-produits' && userRole !== 'admin') return false;
    return true;
  });

  return (
    <div style={styles.layout}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <img src={`${process.env.PUBLIC_URL}/lesiam_logo.jpg`} alt="Le Siam" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: '50%', border: '3px solid #C9A96E', boxShadow: '0 0 20px rgba(201,169,110,0.5), 0 0 50px rgba(201,169,110,0.2)', marginBottom: 10 }} />
          <h2 style={styles.sidebarLogo}>Le Siam</h2>
          <p style={styles.sidebarSub}>Pilotage Interne</p>
        </div>
        {navItems.map(item => (
          <button
            key={item.key}
            style={{ ...styles.navItem, ...(page === item.key ? styles.navItemActive : {}) }}
            onClick={() => setPage(item.key as typeof page)}
          >
            <span>{item.icon}</span> {item.label}
          </button>
        ))}

        <div style={styles.sidebarFooter}>
          <div style={styles.userInfo}>
            <span style={styles.userRole}>{userRole === 'admin' ? 'Administrateur' : 'Personnel'}</span>
            <span style={styles.userName}>{userName}</span>
          </div>
          <button style={styles.logoutBtn} onClick={logout}>Deconnexion</button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.main}>
        {/* DASHBOARD */}
        {page === 'dashboard' && (
          <div>
            <h1 style={styles.pageTitle}>Dashboard</h1>
            {/* KPI Cards */}
            <div style={styles.kpiRow}>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #C9A96E' }}>
                <p style={styles.kpiLabel}>Revenu du jour</p>
                <p style={styles.kpiValue}>{revenueToday.toFixed(2)} EUR</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #2ECC71' }}>
                <p style={styles.kpiLabel}>Check-ins</p>
                <p style={styles.kpiValue}>{checkInsToday}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #3498DB' }}>
                <p style={styles.kpiLabel}>Reservations</p>
                <p style={styles.kpiValue}>{reservationsToday}</p>
              </div>
            </div>

            {/* Top Clients */}
            <h2 style={styles.sectionTitle}>Top Clients du Mois</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead><tr><th style={styles.tableHeader}>Nom</th><th style={styles.tableHeader}>Email</th><th style={styles.tableHeader}>Depenses</th><th style={styles.tableHeader}>Commandes</th><th style={styles.tableHeader}>Points</th></tr></thead>
                <tbody>
                  {topClients.map(c => (
                    <tr key={c.user_id} style={{ cursor: 'pointer' }} onClick={() => { setPage('clients'); loadClientDetail(c.user_id); }}>
                      <td style={styles.tableCell}>{c.name}</td>
                      <td style={styles.tableCell}>{c.email}</td>
                      <td style={{ ...styles.tableCell, color: '#C9A96E', fontWeight: 'bold' }}>{c.total_spent.toFixed(2)} EUR</td>
                      <td style={styles.tableCell}>{c.total_orders}</td>
                      <td style={styles.tableCell}>{c.loyalty_points} pts</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Client Segments */}
            <h2 style={styles.sectionTitle}>Segmentation Client</h2>
            <div style={styles.segmentRow}>
              {segments.map(s => (
                <div key={s.segment} style={styles.segmentCard}>
                  <h3 style={styles.segmentName}>{s.segment}</h3>
                  <p style={styles.segmentCount}>{s.count} clients</p>
                  <p style={styles.segmentAvg}>Moy: {s.avg_spent.toFixed(0)} EUR</p>
                  <p style={styles.segmentDesc}>{s.description}</p>
                </div>
              ))}
            </div>

            {/* Product Performance */}
            <h2 style={styles.sectionTitle}>Performance Produits</h2>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead><tr><th style={styles.tableHeader}>Produit</th><th style={styles.tableHeader}>Categorie</th><th style={styles.tableHeader}>Ventes</th><th style={styles.tableHeader}>CA</th></tr></thead>
                <tbody>
                  {productPerfs.map(p => (
                    <tr key={p.product_id}>
                      <td style={styles.tableCell}>{p.product_name}</td>
                      <td style={styles.tableCell}>{p.category}</td>
                      <td style={styles.tableCell}>{p.total_sold}</td>
                      <td style={{ ...styles.tableCell, color: '#C9A96E' }}>{p.total_revenue.toFixed(2)} EUR</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CLIENTS */}
        {page === 'clients' && (
          <div>
            <h1 style={styles.pageTitle}>Gestion Clients</h1>
            <div style={styles.searchRow}>
              <input style={styles.searchInput} placeholder="Rechercher un client..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); loadClients(e.target.value); }} />
            </div>

            {selectedClient ? (
              <div>
                <button style={styles.backBtn} onClick={() => setSelectedClient(null)}>Retour a la liste</button>
                <div style={styles.clientDetail}>
                  <h2>{selectedClient.first_name} {selectedClient.last_name}</h2>
                  <p>Email: {selectedClient.email} | Tel: {selectedClient.phone}</p>
                  <p>Allergies: {selectedClient.allergies || 'Aucune'}</p>
                  <div style={styles.clientStats}>
                    <div><strong>Total depense:</strong> {selectedClient.total_spent.toFixed(2)} EUR</div>
                    <div><strong>Commandes:</strong> {selectedClient.total_orders}</div>
                    <div><strong>Fidelite:</strong> {selectedClient.loyalty_points} pts</div>
                  </div>
                  <h3 style={{ marginTop: 20 }}>Historique de consommation</h3>
                  {clientConsumption.map((order: any, i: number) => (
                    <div key={i} style={styles.orderHistory}>
                      <div style={{ fontWeight: 'bold' }}>{new Date(order.date).toLocaleString('fr-FR')} - {order.total.toFixed(2)} EUR ({order.payment_method})</div>
                      {order.items.map((item: any, j: number) => (
                        <div key={j} style={{ paddingLeft: 16, color: '#aaa' }}>{item.product_name} x{item.quantity} = {item.subtotal.toFixed(2)} EUR</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead><tr><th style={styles.tableHeader}>Nom</th><th style={styles.tableHeader}>Email</th><th style={styles.tableHeader}>Depenses</th><th style={styles.tableHeader}>Commandes</th><th style={styles.tableHeader}>Fidelite</th><th style={styles.tableHeader}>Derniere visite</th></tr></thead>
                  <tbody>
                    {clients.map((c: any) => (
                      <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => loadClientDetail(c.id)}>
                        <td style={styles.tableCell}>{c.first_name} {c.last_name}</td>
                        <td style={styles.tableCell}>{c.email}</td>
                        <td style={{ ...styles.tableCell, color: '#C9A96E' }}>{c.total_spent.toFixed(2)} EUR</td>
                        <td style={styles.tableCell}>{c.total_orders}</td>
                        <td style={styles.tableCell}>{c.loyalty_points} pts</td>
                        <td style={styles.tableCell}>{c.last_visit ? new Date(c.last_visit).toLocaleDateString('fr-FR') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SOCIAL */}
        {page === 'social' && socialStats && (
          <div>
            <h1 style={styles.pageTitle}>Statistiques Sociales</h1>
            <p style={styles.disclaimer}>Les contenus prives sont inaccessibles au personnel.</p>
            <div style={styles.kpiRow}>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #9B59B6' }}>
                <p style={styles.kpiLabel}>Tentatives</p>
                <p style={styles.kpiValue}>{socialStats.total_attempts}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #E67E22' }}>
                <p style={styles.kpiLabel}>Interactions</p>
                <p style={styles.kpiValue}>{socialStats.total_interactions}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #2ECC71' }}>
                <p style={styles.kpiLabel}>Matchs</p>
                <p style={styles.kpiValue}>{socialStats.total_matches}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #C9A96E' }}>
                <p style={styles.kpiLabel}>Actifs maintenant</p>
                <p style={styles.kpiValue}>{socialStats.active_social_users}</p>
              </div>
            </div>
          </div>
        )}

        {/* NOTIFICATIONS */}
        {page === 'notifications' && (
          <div>
            <h1 style={styles.pageTitle}>Gestion des Notifications</h1>
            <div style={styles.notifActions}>
              <button style={styles.notifBtn} onClick={sendBirthdayNotifs}>Anniversaires J-2</button>
              <button style={styles.notifBtn} onClick={sendPromo}>Promotion</button>
            </div>
          </div>
        )}

        {/* GESTION PRODUITS - Admin Only */}
        {page === 'gestion-produits' && userRole === 'admin' && (
          <div>
            <h1 style={styles.pageTitle}>Gestion Produits</h1>

            {/* Stats KPIs */}
            <div style={styles.kpiRow}>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #C9A96E' }}>
                <p style={styles.kpiLabel}>Total Produits</p>
                <p style={styles.kpiValue}>{allProducts.length}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #2ECC71' }}>
                <p style={styles.kpiLabel}>Disponibles</p>
                <p style={styles.kpiValue}>{allProducts.filter(p => p.is_available).length}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #E74C3C' }}>
                <p style={styles.kpiLabel}>Indisponibles</p>
                <p style={styles.kpiValue}>{allProducts.filter(p => !p.is_available).length}</p>
              </div>
              <div style={{ ...styles.kpiCard, borderLeft: '4px solid #3498DB' }}>
                <p style={styles.kpiLabel}>Traiteur</p>
                <p style={styles.kpiValue}>{allProducts.filter(p => p.is_catering).length}</p>
              </div>
            </div>

            {/* Filters + Add */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const, alignItems: 'center' }}>
              <input
                style={styles.searchInput}
                placeholder="Rechercher un produit..."
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
              />
              <select
                style={{ ...styles.configInput, width: 200, marginBottom: 0 }}
                value={productCategoryFilter}
                onChange={e => setProductCategoryFilter(e.target.value)}
              >
                <option value="all">Toutes categories</option>
                <option value="wine">Vin</option>
                <option value="food">Nourriture</option>
                <option value="cocktail">Cocktail</option>
                <option value="beverage">Boisson</option>
                <option value="catering">Traiteur</option>
              </select>
              <button style={styles.configBtn} onClick={() => setShowAddProduct(!showAddProduct)}>
                {showAddProduct ? 'Annuler' : '+ Ajouter un produit'}
              </button>
              <button style={{ ...styles.configBtn, backgroundColor: '#3498DB' }} onClick={() => loadAllProducts()}>
                Rafraichir
              </button>
            </div>

            {/* Add Product Form */}
            {showAddProduct && (
              <div style={styles.configForm}>
                <h3 style={{ color: '#C9A96E', marginTop: 0, marginBottom: 12 }}>Nouveau Produit</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input style={styles.configInput} placeholder="Nom du produit" value={newProductName} onChange={e => setNewProductName(e.target.value)} />
                  <select style={styles.configInput} value={newProductCategory} onChange={e => setNewProductCategory(e.target.value)}>
                    <option value="wine">Vin</option>
                    <option value="food">Nourriture</option>
                    <option value="cocktail">Cocktail</option>
                    <option value="beverage">Boisson</option>
                    <option value="catering">Traiteur</option>
                    <option value="other">Autre</option>
                  </select>
                  <input style={styles.configInput} type="number" step="0.01" placeholder="Prix de vente (EUR)" value={newProductPrice} onChange={e => setNewProductPrice(e.target.value)} />
                  <input style={styles.configInput} placeholder="Sous-categorie" value={newProductSubCategory} onChange={e => setNewProductSubCategory(e.target.value)} />
                  <input style={styles.configInput} placeholder="Description" value={newProductDescription} onChange={e => setNewProductDescription(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' as const }}>
                  <label style={{ color: '#F5F5F5', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={newProductAvailable} onChange={e => setNewProductAvailable(e.target.checked)} /> Disponible
                  </label>
                  <label style={{ color: '#F5F5F5', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={newProductFeatured} onChange={e => setNewProductFeatured(e.target.checked)} /> Mis en avant
                  </label>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button style={styles.configBtn} onClick={handleCreateProduct}>Creer le produit</button>
                </div>
              </div>
            )}

            {/* Products Table */}
            <div style={{ ...styles.tableContainer, overflowX: 'auto' as const }}>
              <table style={{ ...styles.table, minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, whiteSpace: 'nowrap' as const }}>ID</th>
                    <th style={styles.tableHeader}>Nom</th>
                    <th style={styles.tableHeader}>Categorie</th>
                    <th style={styles.tableHeader}>Prix vente</th>
                    <th style={styles.tableHeader}>Cout achat</th>
                    <th style={styles.tableHeader}>Marge %</th>
                    <th style={styles.tableHeader}>Cat. traiteur</th>
                    <th style={styles.tableHeader}>Dispo</th>
                    <th style={styles.tableHeader}>Ordre</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allProducts
                    .filter(p => {
                      if (productCategoryFilter !== 'all' && p.category !== productCategoryFilter) return false;
                      if (productSearch && !p.name.toLowerCase().includes(productSearch.toLowerCase())) return false;
                      return true;
                    })
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                    .map(p => {
                      const margin = p.price > 0 && p.cost_price != null
                        ? ((p.price - p.cost_price) / p.price * 100).toFixed(1)
                        : '-';
                      const isEditing = editProductId === p.id;
                      return (
                        <tr key={p.id} style={isEditing ? { backgroundColor: '#1a1a4e' } : {}}>
                          <td style={{ ...styles.tableCell, fontSize: 12, color: '#666' }}>{p.id}</td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <input style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 150 }} value={editProductData.name ?? p.name} onChange={e => setEditProductData({ ...editProductData, name: e.target.value })} />
                              : <span style={{ fontWeight: 'bold' as const }}>{p.name}</span>
                            }
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <select style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 120 }} value={editProductData.category ?? p.category} onChange={e => setEditProductData({ ...editProductData, category: e.target.value })}>
                                  <option value="wine">Vin</option>
                                  <option value="food">Nourriture</option>
                                  <option value="cocktail">Cocktail</option>
                                  <option value="beverage">Boisson</option>
                                  <option value="catering">Traiteur</option>
                                  <option value="other">Autre</option>
                                </select>
                              : <span style={{ color: '#3498DB' }}>{p.category}</span>
                            }
                          </td>
                          <td style={{ ...styles.tableCell, color: '#C9A96E', fontWeight: 'bold' as const }}>
                            {isEditing
                              ? <input style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 80 }} type="number" step="0.01" value={editProductData.price ?? p.price} onChange={e => setEditProductData({ ...editProductData, price: parseFloat(e.target.value) || 0 })} />
                              : `${p.price.toFixed(2)} EUR`
                            }
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <input style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 80 }} type="number" step="0.01" placeholder="0.00" value={editProductData.cost_price ?? (p.cost_price ?? '')} onChange={e => setEditProductData({ ...editProductData, cost_price: parseFloat(e.target.value) || null })} />
                              : (p.cost_price != null ? `${p.cost_price.toFixed(2)} EUR` : <span style={{ color: '#666' }}>—</span>)
                            }
                          </td>
                          <td style={{
                            ...styles.tableCell,
                            color: margin !== '-'
                              ? (parseFloat(margin as string) >= 60 ? '#2ECC71' : parseFloat(margin as string) >= 30 ? '#C9A96E' : '#E74C3C')
                              : '#666',
                            fontWeight: 'bold' as const
                          }}>
                            {margin !== '-' ? `${margin}%` : '—'}
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <select style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 140 }} value={editProductData.catering_category ?? (p.catering_category ?? '')} onChange={e => setEditProductData({ ...editProductData, catering_category: e.target.value || null })}>
                                  <option value="">Aucune</option>
                                  <option value="rotisserie_poulet">Rotisserie Poulet</option>
                                  <option value="canards_entiers">Canards Entiers</option>
                                  <option value="plats_chauds">Plats Chauds</option>
                                  <option value="soupes_thai">Soupes Thai</option>
                                  <option value="tapas">Tapas & Nems</option>
                                  <option value="brochettes_satay">Brochettes & Satay</option>
                                  <option value="boulettes">Boulettes</option>
                                  <option value="verrines">Verrines & Mini-portions</option>
                                  <option value="wok_siam">Wok Siam</option>
                                </select>
                              : <span style={{ fontSize: 12 }}>{p.catering_category ? p.catering_category.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : '—'}</span>
                            }
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <input type="checkbox" checked={editProductData.is_available ?? p.is_available} onChange={e => setEditProductData({ ...editProductData, is_available: e.target.checked })} />
                              : <span style={{ color: p.is_available ? '#2ECC71' : '#E74C3C', fontWeight: 'bold' as const }}>{p.is_available ? 'Oui' : 'Non'}</span>
                            }
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <input style={{ ...styles.configInput, margin: 0, padding: '4px 8px', width: 60 }} type="number" value={editProductData.sort_order ?? p.sort_order} onChange={e => setEditProductData({ ...editProductData, sort_order: parseInt(e.target.value) || 0 })} />
                              : p.sort_order
                            }
                          </td>
                          <td style={styles.tableCell}>
                            {isEditing
                              ? <div style={{ display: 'flex', gap: 6 }}>
                                  <button style={{ ...styles.configBtn, padding: '4px 10px', fontSize: 12 }} onClick={() => handleUpdateProduct(p.id)}>Sauver</button>
                                  <button style={{ ...styles.configBtnSmall, fontSize: 11 }} onClick={() => { setEditProductId(null); setEditProductData({}); }}>Annuler</button>
                                </div>
                              : <div style={{ display: 'flex', gap: 6 }}>
                                  <button style={{ ...styles.configBtn, padding: '4px 10px', fontSize: 12 }} onClick={() => { setEditProductId(p.id); setEditProductData({}); }}>Modifier</button>
                                  <button style={styles.configBtnSmall} onClick={() => handleDeleteProduct(p.id)}>Desact.</button>
                                </div>
                            }
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Import CSV (reuse handler from Config) */}
            <div style={{ ...styles.configSection, marginTop: 24 }}>
              <h2 style={styles.configSectionTitle}>Import Rapide (CSV)</h2>
              <p style={{ color: '#A0A0A0', fontSize: 13, marginBottom: 12 }}>
                Format: name, category, sub_category, description, price, is_available, is_featured, sort_order
              </p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="file" accept=".csv" style={{ color: '#A0A0A0' }} onChange={e => setCsvImportFile(e.target.files?.[0] || null)} />
                <button style={styles.configBtn} onClick={async () => { await handleImportCsv(csvImportFile, setCsvImportResult); loadAllProducts(); }}>Importer CSV</button>
              </div>
              {csvImportResult && <p style={{ color: '#F5F5F5', marginTop: 8 }}>{csvImportResult}</p>}
            </div>
          </div>
        )}

        {/* CONFIGURATION - Admin Only */}
        {page === 'config' && userRole === 'admin' && (
          <div>
            <h1 style={styles.pageTitle}>Configuration</h1>

            {/* SECTION A - Gestion Clients */}
            <div style={styles.configSection}>
              <h2 style={styles.configSectionTitle}>Gestion Clients</h2>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
                <button style={styles.configBtn} onClick={() => setShowAddClient(!showAddClient)}>
                  {showAddClient ? 'Annuler' : 'Ajouter un client'}
                </button>
                <button style={styles.configBtn} onClick={handleExportClients}>Exporter CSV</button>
              </div>

              {showAddClient && (
                <div style={styles.configForm}>
                  <input style={styles.configInput} placeholder="Email" value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)} />
                  <input style={styles.configInput} type="password" placeholder="Mot de passe" value={newClientPassword} onChange={e => setNewClientPassword(e.target.value)} />
                  <input style={styles.configInput} placeholder="Prenom" value={newClientFirstName} onChange={e => setNewClientFirstName(e.target.value)} />
                  <input style={styles.configInput} placeholder="Nom" value={newClientLastName} onChange={e => setNewClientLastName(e.target.value)} />
                  <input style={styles.configInput} placeholder="Telephone" value={newClientPhone} onChange={e => setNewClientPhone(e.target.value)} />
                  <button style={styles.configBtn} onClick={handleCreateClient}>Creer le client</button>
                </div>
              )}

              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead><tr><th style={styles.tableHeader}>Nom</th><th style={styles.tableHeader}>Email</th><th style={styles.tableHeader}>Telephone</th><th style={styles.tableHeader}>Actions</th></tr></thead>
                  <tbody>
                    {clients.map((c: any) => (
                      <tr key={c.id}>
                        <td style={styles.tableCell}>{c.first_name} {c.last_name}</td>
                        <td style={styles.tableCell}>{c.email}</td>
                        <td style={styles.tableCell}>{c.phone || '-'}</td>
                        <td style={styles.tableCell}>
                          <button style={styles.configBtnSmall} onClick={() => handleDisableClient(c.id)}>Desactiver</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* SECTION B - Gestion Produits */}
            <div style={styles.configSection}>
              <h2 style={styles.configSectionTitle}>Gestion Produits</h2>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const }}>
                <button style={styles.configBtn} onClick={() => setShowAddProduct(!showAddProduct)}>
                  {showAddProduct ? 'Annuler' : 'Ajouter un produit'}
                </button>
              </div>

              {showAddProduct && (
                <div style={styles.configForm}>
                  <input style={styles.configInput} placeholder="Nom du produit" value={newProductName} onChange={e => setNewProductName(e.target.value)} />
                  <select style={styles.configInput} value={newProductCategory} onChange={e => setNewProductCategory(e.target.value)}>
                    <option value="wine">Vin</option>
                    <option value="food">Nourriture</option>
                    <option value="cocktail">Cocktail</option>
                    <option value="beverage">Boisson</option>
                  </select>
                  <input style={styles.configInput} type="number" step="0.01" placeholder="Prix" value={newProductPrice} onChange={e => setNewProductPrice(e.target.value)} />
                  <input style={styles.configInput} placeholder="Description" value={newProductDescription} onChange={e => setNewProductDescription(e.target.value)} />
                  <input style={styles.configInput} placeholder="Sous-categorie" value={newProductSubCategory} onChange={e => setNewProductSubCategory(e.target.value)} />
                  <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 12 }}>
                    <label style={{ color: '#F5F5F5', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={newProductAvailable} onChange={e => setNewProductAvailable(e.target.checked)} /> Disponible
                    </label>
                    <label style={{ color: '#F5F5F5', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={newProductFeatured} onChange={e => setNewProductFeatured(e.target.checked)} /> Mis en avant
                    </label>
                  </div>
                  <button style={styles.configBtn} onClick={handleCreateProduct}>Creer le produit</button>
                </div>
              )}

              <div style={{ marginTop: 20 }}>
                <p style={{ color: '#A0A0A0', fontSize: 14, marginBottom: 8 }}>Importer des produits via CSV</p>
                <input type="file" accept=".csv" style={{ color: '#A0A0A0', marginBottom: 8 }} onChange={e => setCsvImportFile(e.target.files?.[0] || null)} />
                <button style={styles.configBtn} onClick={() => handleImportCsv(csvImportFile, setCsvImportResult)}>Importer CSV</button>
                {csvImportResult && <p style={{ color: '#F5F5F5', marginTop: 8 }}>{csvImportResult}</p>}
              </div>
            </div>

            {/* SECTION C - Import Rapide CSV */}
            <div style={styles.configSection}>
              <h2 style={styles.configSectionTitle}>Import Rapide (CSV)</h2>
              <p style={{ color: '#A0A0A0', fontSize: 13, marginBottom: 12 }}>
                Format CSV attendu: name, category, sub_category, description, price, is_available, is_featured, sort_order
              </p>
              <input type="file" accept=".csv" style={{ color: '#A0A0A0', marginBottom: 8 }} onChange={e => setQuickCsvFile(e.target.files?.[0] || null)} />
              <button style={styles.configBtn} onClick={() => handleImportCsv(quickCsvFile, setQuickCsvResult)}>Importer produits CSV</button>
              {quickCsvResult && <p style={{ color: '#F5F5F5', marginTop: 8 }}>{quickCsvResult}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== STYLES ====================
const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', backgroundColor: '#0F0F23', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  loginCard: { backgroundColor: '#1A1A3A', borderRadius: 16, padding: 48, width: 400, textAlign: 'center' as const },
  logo: { color: '#C9A96E', fontSize: 36, fontWeight: 'bold' as const, margin: 0 },
  logoSub: { color: '#A0A0A0', letterSpacing: 4, marginBottom: 32 },
  input: { width: '100%', backgroundColor: '#16213E', border: '1px solid #2A2A4A', borderRadius: 8, padding: 12, color: '#F5F5F5', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' as const },
  loginBtn: { width: '100%', backgroundColor: '#C9A96E', color: '#1A1A2E', border: 'none', borderRadius: 10, padding: 14, fontSize: 16, fontWeight: 'bold' as const, cursor: 'pointer' },
  loginError: { color: '#E74C3C', fontSize: 13, marginTop: 16,fontWeight: 'bold' as const },
  layout: { display: 'flex', minHeight: '100vh', backgroundColor: '#0F0F23' },
  sidebar: { width: 240, backgroundColor: '#1A1A3A', borderRight: '1px solid #2A2A4A', display: 'flex', flexDirection: 'column' as const },
  sidebarHeader: { padding: 24, borderBottom: '1px solid #2A2A4A' },
  sidebarLogo: { color: '#C9A96E', margin: 0, fontSize: 22 },
  sidebarSub: { color: '#A0A0A0', fontSize: 11, letterSpacing: 2 },
  navItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 24px', backgroundColor: 'transparent', border: 'none', color: '#A0A0A0', fontSize: 14, cursor: 'pointer', textAlign: 'left' as const, width: '100%' },
  navItemActive: { backgroundColor: '#C9A96E20', color: '#C9A96E', borderRight: '3px solid #C9A96E' },
  sidebarFooter: { marginTop: 'auto', padding: 24, borderTop: '1px solid #2A2A4A' },
  userInfo: { display: 'flex', flexDirection: 'column' as const, marginBottom: 12, gap: 4 },
  userRole: { color: '#C9A96E', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 1, fontWeight: 'bold' as const },
  userName: { color: '#F5F5F5', fontSize: 14 },
  logoutBtn: { backgroundColor: 'transparent', border: '1px solid #E74C3C', color: '#E74C3C', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', width: '100%' },
  main: { flex: 1, padding: 32, overflowY: 'auto' as const },
  pageTitle: { color: '#C9A96E', fontSize: 28, fontWeight: 'bold' as const, marginBottom: 24 },
  sectionTitle: { color: '#F5F5F5', fontSize: 18, fontWeight: 'bold' as const, marginTop: 32, marginBottom: 16 },
  kpiRow: { display: 'flex', gap: 20, marginBottom: 32, flexWrap: 'wrap' as const },
  kpiCard: { backgroundColor: '#1A1A3A', borderRadius: 12, padding: 24, flex: 1, minWidth: 180 },
  kpiLabel: { color: '#A0A0A0', fontSize: 12, margin: 0, marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: 1 },
  kpiValue: { color: '#F5F5F5', fontSize: 28, fontWeight: 'bold' as const, margin: 0 },
  tableContainer: { backgroundColor: '#1A1A3A', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  tableCell: { padding: '12px 16px', color: '#F5F5F5', fontSize: 14, borderBottom: '1px solid #2A2A4A' },
  segmentRow: { display: 'flex', gap: 16, flexWrap: 'wrap' as const },
  segmentCard: { backgroundColor: '#1A1A3A', borderRadius: 12, padding: 20, flex: 1, minWidth: 200 },
  segmentName: { color: '#C9A96E', margin: 0, fontSize: 18 },
  segmentCount: { color: '#F5F5F5', fontSize: 24, fontWeight: 'bold' as const, margin: '8px 0' },
  segmentAvg: { color: '#2ECC71', margin: '4px 0', fontSize: 14 },
  segmentDesc: { color: '#A0A0A0', fontSize: 12 },
  searchRow: { marginBottom: 20 },
  searchInput: { width: 300, backgroundColor: '#1A1A3A', border: '1px solid #2A2A4A', borderRadius: 8, padding: 10, color: '#F5F5F5', fontSize: 14 },
  backBtn: { backgroundColor: 'transparent', border: '1px solid #C9A96E', color: '#C9A96E', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', marginBottom: 16 },
  clientDetail: { backgroundColor: '#1A1A3A', borderRadius: 12, padding: 24, color: '#F5F5F5' },
  clientStats: { display: 'flex', gap: 24, marginTop: 16, padding: 16, backgroundColor: '#0F0F23', borderRadius: 8 },
  orderHistory: { padding: 12, borderBottom: '1px solid #2A2A4A', color: '#F5F5F5' },
  disclaimer: { color: '#A0A0A0', fontSize: 12, fontStyle: 'italic', marginBottom: 24 },
  notifActions: { display: 'flex', gap: 16 },
  notifBtn: { backgroundColor: '#C9A96E', color: '#1A1A2E', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 14, fontWeight: 'bold' as const, cursor: 'pointer' },
  configSection: { backgroundColor: '#1A1A3A', borderRadius: 12, padding: 24, marginBottom: 24, border: '1px solid #2A2A5E' },
  configSectionTitle: { color: '#C9A96E', fontSize: 20, fontWeight: 'bold' as const, marginTop: 0, marginBottom: 16, borderBottom: '1px solid #2A2A5E', paddingBottom: 12 },
  configBtn: { backgroundColor: '#C9A96E', color: '#0F0F23', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 'bold' as const, cursor: 'pointer' },
  configBtnSmall: { backgroundColor: 'transparent', border: '1px solid #E74C3C', color: '#E74C3C', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  configInput: { width: '100%', backgroundColor: '#1a1a3e', border: '1px solid #2a2a5e', borderRadius: 8, padding: '8px 12px', color: 'white', fontSize: 14, marginBottom: 10, boxSizing: 'border-box' as const },
  configForm: { backgroundColor: '#0F0F23', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #2A2A5E' },
  tableHeader: { color: '#50C878', fontWeight: 'bold' as const, padding: '12px 16px', textAlign: 'left' as const, borderBottom: '2px solid rgba(80,200,120,0.25)' },
};

export default App;